import React from "react";
import { useState, useEffect, useRef } from "react";
import "../assets/css/style.scss"
import logo from '../assets/images/logo.png'
import hero_image from '../assets/images/rafi_profile.png'
import launch from "../assets/images/launch.png"
import githubLogo from "../assets/images/github-icon-1.svg"
import linkedinLogo from "../assets/images/linkedin-icon-2.svg"
import leetcodeLogo from "../assets/images/leetcode-1.svg"
import codeforcesLogo from "../assets/images/code-forces.svg"
import codechefLogo from "../assets/images/icons8-codechef.svg"

const skillCategories = [
    {
        title: "Programming Language",
        skills: ["Ruby", "C/C++", "Javascript", "Python"],
    },
    {
        title: "Frameworks & Libraries",
        skills: ["Ruby on Rails", "React.js", "Node.js", "Express.js", "Bootstrap", "JQuery"],
    },
    {
        title: "DevOps",
        skills: ["Git", "Github CI/CD", "GCP", "Docker", "Kubernetes", "Shipit", "Linux"],
    },
    {
        title: "Database",
        skills: ["PostgreSQL", "MySQL", "MongoDB"],
    },
];
const talkingPoints = ['Travel', 'Chess', 'WebDev', 'Football', 'Coding', 'Movie'];

const App = () => {
    const trackRef = useRef(null);
    const canvasRef = useRef(null);
    const paragraphRef = useRef(null);
    
    const total = skillCategories.length;
    const autoSlideTime = 3000;
    const visibleCards = 3;
    
    const [loading, setLoading] = useState(true);
    const [cardWidth, setCardWidth] = useState(420);
    const [topicIndex, setTopicIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState(1);

    const [index, setIndex] = useState(total - 1); // Start at the first original card in the center

    // Clone last few and first few for seamless loop
    const extendedCards = [
        ...skillCategories.slice(-visibleCards), // clone last 3
        ...skillCategories,
        ...skillCategories.slice(0, visibleCards), // clone first 3
    ];
    
    // For loader
    useEffect(() => {
        const handleLoad = () => {
            setTimeout(() => setLoading(false), 500);
        };
    
        if (document.readyState === "complete") {
            handleLoad();
        } else {
            window.addEventListener("load", handleLoad);
            return () => window.removeEventListener("load", handleLoad);
        }
    }, []);


    useEffect(() => {
        const interval = setInterval(() => {
            setIndex(prev => prev + 1);
        }, autoSlideTime);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const updateCardWidth = () => {
            const width = window.innerWidth;

            if (width <= 600) {
                setCardWidth(320);
            }
            else if (width <= 1200) {
                setCardWidth(400);
            }
            else if (width <= 1400){ 
                setCardWidth(360);
            }
            else {
                setCardWidth(420);
            }
        };
    
        updateCardWidth(); // Initial run
        window.addEventListener('resize', updateCardWidth);
        return () => window.removeEventListener('resize', updateCardWidth);
    }, []);

    useEffect(() => {
        const track = trackRef.current;
        
        track.style.transition = 'transform 0.5s ease';
        track.style.transform = `translateX(-${index * cardWidth}px)`;
    
        // Instant jump if we've gone too far left or right
        const handleTransitionEnd = () => {
            track.removeEventListener('transitionend', handleTransitionEnd);
            
            if (index >= total + visibleCards) {
              track.style.transition = 'none';
              track.style.transform = `translateX(-${(total - 1) * cardWidth}px)`;
        
              // Delay state update so it doesn't trigger a render during transform
              setTimeout(() => setIndex(total - 1), 0);
            }
        
            if (index <= visibleCards - 1) {
              track.style.transition = 'none';
              track.style.transform = `translateX(-${(total - 1) * cardWidth}px)`;
        
              setTimeout(() => setIndex(total - 1), 0);
            }
          };
    
        track.addEventListener('transitionend', handleTransitionEnd);

        return () => {
            track.removeEventListener('transitionend', handleTransitionEnd);
          };
      }, [index, total, visibleCards, cardWidth]);

    useEffect(() => {
        const interval = setInterval(() => {
            setTopicIndex((prevIndex) => {
                const nextIndex = prevIndex + slideDirection;

                if(nextIndex >= talkingPoints.length){
                    setSlideDirection(-1);
                    return prevIndex - 1;
                }
                if(nextIndex < 0){
                    setSlideDirection(1);
                    return prevIndex + 1;
                }

                return nextIndex;
            });
        }, 2000);

        return () => clearInterval(interval);
    }, [slideDirection]);

    // For Rocket Canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        const container = paragraphRef.current;
        const ctx = canvas.getContext("2d");
    
        const rocketImg = new Image();
        rocketImg.src = launch;
    
        let mouse = { x: 0, y: 0 };
        let rocketPos = { x: 0, y: 0, angle: 0 };
    
        // Resize canvas
        const resizeCanvas = () => {
            canvas.width = container.clientWidth;
            canvas.height = container.clientHeight;
        };

        const handleMouseMove = (e) => {
            const rect = container.getBoundingClientRect();
            mouse.x = e.clientX - rect.left;
            mouse.y = e.clientY - rect.top;
        };

        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            rocketPos.x += (mouse.x - rocketPos.x) * 0.1;
            rocketPos.y += (mouse.y - rocketPos.y) * 0.1;

            rocketPos.angle = Math.atan2(
                mouse.y - rocketPos.y,
                mouse.x - rocketPos.x
            );
            
            ctx.save();
            ctx.translate(rocketPos.x, rocketPos.y);
            ctx.rotate(rocketPos.angle);
            ctx.drawImage(rocketImg, -22, -22, 44, 44);
            ctx.restore();

            requestAnimationFrame(animate);
        }

        resizeCanvas();
        window.addEventListener("resize", resizeCanvas);
        container.addEventListener("mousemove", handleMouseMove);
        rocketImg.onload = animate;
    
        return () => {
            container.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("resize", resizeCanvas);
        };
    }, []);

    return (
    <React.Fragment>
        {loading && (
            <div className="loader-wrapper">
                <div className="loader-banner">
                    <p className="loader-slide">
                        Loading Loading Loading Loading Loading Loading Loading Loading
                        Loading Loading Loading Loading Loading Loading Loading Loading
                        Loading Loading Loading Loading Loading Loading Loading Loading
                    </p>
                </div>
            </div>
        )}
        <nav className='nav'>
            <img className="nav-logo" src={logo} alt="Logo" />
        </nav>
        <section className="hero no-select">
            <div className="hero-background">
                <div className="hero-background-text">
                    <div className="slide-animation">
                        Software Engineer Software Engineer Software Engineer Software Engineer
                        Software Engineer Software Engineer Software Engineer Software Engineer
                        Software Engineer Software Engineer Software Engineer Software Engineer
                    </div>
                    <div className="slide-animation">
                        Problem Solver Problem Solver Problem Solver Problem Solver Problem Solver
                        Problem Solver Problem Solver Problem Solver Problem Solver Problem Solver
                        Problem Solver Problem Solver Problem Solver Problem Solver Problem Solver
                    </div>
                    <div className="slide-animation">
                        Web Developer Web Developer Web Developer Web Developer Web Developer
                        Web Developer Web Developer Web Developer Web Developer Web Developer
                        Web Developer Web Developer Web Developer Web Developer Web Developer
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="hero-wrapper">
                    <img className="hero-wrapper-picture" src={hero_image} alt="hero" />
                </div>
            </div>
        </section>
        <section className='introduction'>
            <div className="container">
                <h1>
                    <div>
                        H! let’s explore what drives
                    </div>
                    <div>
                        <span className="hover-gif"> Imtiaj Rafi</span>.
                    </div>
                </h1>
            </div>
        </section>
        <section className="skills">
            <div className="skills-paragraph" ref={paragraphRef} style={{ position: "relative" }}>
                <canvas id="rocketCanvas" ref={canvasRef}></canvas>
                <div className="container">
                    <p className="no-select">
                        A <span>Software Engineer</span> who loves <span>Problem Solving</span>,<span> Web Development </span>&<span> Traveling</span>.
                        <br /><br />
                        
                        With <span>2000+</span> problems solved across various platform, I’ve attended numerous contests
                        including <span>ICPC Regional</span>, IUPC's & online competitions.
                        <br /><br />
                        
                        I specialize in building scalable & efficient APIs with <span>Ruby on Rails</span>, ensuring 
                        seamless backend performance for web applications. Also love crafting sleek,
                        dynamic websites with <span>React.js</span> like this portfolio & used <span>Netlify</span> for hosting. 
                        <br /><br />
                        
                        Besides coding, you’ll probably find me traveling or planning my next adventure.
                    </p>
                </div>
            </div>
            <div className="container">
                <h2>I can work with</h2>
                <div className="skills-slider-container no-select">
                    <div className="skills-slider" ref={trackRef}>
                        {extendedCards.map((category, index) => (
                            <div className="skills-slider-card" key={index}>
                                <h3>{category.title}</h3>
                                <ul>
                                    {category.skills.map((skill, idx) => (
                                        <li key={idx}>{skill}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
        <section className="contact">
            <div className="container">
                <div className="contact-pre-title">
                    Wanna know my secrets?
                </div>
                <h2 className="contact-title">
                    <a href="mailto:imtiajrafi.cse@gmail.com" className="contact-title-links">
                        Let's talk about

                        <span className="contact-topic">
                            <ul className="contact-topic-slider">
                                {talkingPoints.map((topic, index)=> {
                                    let className = "contact-anim";

                                    if(index === topicIndex) {
                                        className += " active";
                                    } else if (index < topicIndex) {
                                        className += " offscreen-left";
                                    }else if(index > topicIndex) {
                                        className += " offscreen-right";
                                    }

                                    return(
                                    <li 
                                        key={index}
                                        className={className}
                                    >
                                        {topic}
                                    </li>
                                    );
                                })}
                            </ul>
                        </span>
                    </a>
                </h2>
                <ul className="contact-post-title">
                    <li>
                        <a className="btn" target="_blank" rel="noreferrer" href="https://github.com/Imtiaz-Rafi">
                            <img className="logo" src={githubLogo} alt="Github logo" width={30} height={30}/>
                            <span className="text">Github</span>
                        </a>
                        <a className="btn" target="_blank" rel="noreferrer" href="https://www.linkedin.com/in/imtiaz-rafi/">
                            <img className="logo" src={linkedinLogo} alt="Linkedin logo" width={30} height={30}/>
                            <span className="text">Linkedin</span>
                        </a>
                        <a className="btn" target="_blank" rel="noreferrer" href="https://leetcode.com/u/Imtiaj_Rafi/">
                            <img className="logo" src={leetcodeLogo} alt="Leetcode logo" width={30} height={30}/>
                            <span className="text">Leetcode</span>
                        </a>
                        <a className="btn" target="_blank" rel="noreferrer" href="https://codeforces.com/profile/Imtiaz_rafi">
                            <img className="logo" src={codeforcesLogo} alt="Codeforces logo" width={30} height={30}/>
                            <span className="text">Codeforces</span>
                        </a>
                        <a className="btn" target="_blank" rel="noreferrer" href="https://www.codechef.com/users/imtiazrafi">
                            <img className="logo" src={codechefLogo} alt="Codechef logo" width={30} height={30}/>
                            <span className="text">Codechef</span>
                        </a>
                    </li>
                </ul>
            </div>
        </section>
        <section className="footer">
            <p><span>Arigato gozaimasu</span> if you come till this end.</p>
        </section>
    </React.Fragment>
    )
}

export default App;